import {
  Alert,
  Box,
  Button,
  FileButton,
  Loader,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import React, {useEffect, useRef} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ScientificModelRegistry,
  useScientificModelRegistry,
} from 'model-hub/scientific-models/useAvailableScientificModels';
import getTestProps from 'test/lib/cypress/getTestProps';

import FolderEditor from '../editors/FolderEditor';
import SearchEditor from '../editors/SearchEditor';
import StepConfigFieldsTemplate from '../editors/StepConfigFieldsTemplate';
import {maybeGetValueFromConfig} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
} from '../StepConfigEditorTypes';

const MODEL_CONFIG_KEY = 'modelConfig';
const PREDICTION_BATCH_FOLDER_ID = 'predictionBatchFolderId';
const PREDICTION_BATCH_NAME = 'predictionBatchName';
const PREDICTION_BATCH_NAME_MAX_LENGTH = 255;

const MIN_DESIGNS = 1;
// Keep in sync with numDesigns in benchling/model_hub/scientific_models/models/gsk/boltzgen.py
const MAX_DESIGNS = 1000;

const INPUT_TYPE_OPTIONS = [
  {value: 'pdb', label: 'PDB'},
  {value: 'cif', label: 'CIF'},
];

// Extends `Record` so the interface is assignable to `JSONValue`.
interface TargetInput extends Record<string, string> {
  _type: 'fileId';
  id: string;
}

const DEFAULT_RUN_CONFIG = {
  target: null as TargetInput | null,
  inputType: 'pdb',
  designYaml: '',
  numDesigns: MIN_DESIGNS,
};

function CustomModelConfigEditor(props: CustomComponentEditorProps): JSX.Element {
  const registryResult = useScientificModelRegistry();

  let component: JSX.Element;
  if (registryResult.type === 'loading') {
    component = <Loader size="sm" role="status" aria-label="Loading models" />;
  } else if (registryResult.type === 'error') {
    component = (
      <Alert color="red" role="alert">
        Unable to load models. Please refresh the page.
      </Alert>
    );
  } else {
    component = <LoadedCustomModelConfigEditor {...props} registry={registryResult.registry} />;
  }

  return <Box {...getTestProps({component: 'CustomModelConfigEditor', element: 'root'})}>{component}</Box>;
}

function LoadedCustomModelConfigEditor({
  config,
  onChangeConfig,
  disabled,
  registry,
}: CustomComponentEditorProps & {registry: ScientificModelRegistry}): JSX.Element {
  const predictionBatchFolderId = maybeGetValueFromConfig(config, [PREDICTION_BATCH_FOLDER_ID]) as
    | string
    | null;
  const predictionBatchName =
    (maybeGetValueFromConfig(config, [PREDICTION_BATCH_NAME]) as string | null) ?? '';
  const modelConfig = maybeGetValueFromConfig(config, [MODEL_CONFIG_KEY]);
  const savedModelReference =
    modelConfig != null && typeof modelConfig === 'object' && !Array.isArray(modelConfig)
      ? (modelConfig as {model?: unknown}).model
      : undefined;

  // Spreading the saved config keeps keys this editor doesn't render.
  const runConfig = {
    ...DEFAULT_RUN_CONFIG,
    ...(modelConfig as {config?: Partial<typeof DEFAULT_RUN_CONFIG>} | null)?.config,
  };

  useEffect(() => {
    const defaultFolderId = FolderDefaults.get(FolderDefaultsKey.BULK_MODEL_PREDICTION);
    if (!disabled && predictionBatchFolderId == null && defaultFolderId != null) {
      onChangeConfig(config, [PREDICTION_BATCH_FOLDER_ID], defaultFolderId);
    }
  }, [config, disabled, onChangeConfig, predictionBatchFolderId]);

  const selectedModelReference = typeof savedModelReference === 'string' ? savedModelReference : null;

  // Reading a file is async, so a callback that closed over render-time values could overwrite changes
  // made while the read was pending. Callbacks read the latest values through this ref instead.
  const latestRef = useRef({config, onChangeConfig, runConfig, selectedModelReference});
  latestRef.current = {config, onChangeConfig, runConfig, selectedModelReference};
  const designYamlReadIdRef = useRef(0);

  const selectedModel =
    selectedModelReference == null
      ? undefined
      : registry.catalogByExternalReference.get(selectedModelReference);

  // A model that was saved on the step stays selectable even if it is no longer installed.
  const modelChoices = [...registry.availableCustomModels];
  if (
    selectedModel != null &&
    !modelChoices.some((model) => model.externalReference === selectedModel.externalReference)
  ) {
    modelChoices.push(selectedModel);
  }

  if (modelChoices.length === 0) {
    return <Text size="sm">No custom models are available.</Text>;
  }

  const updateRunConfig = (patch: Partial<typeof DEFAULT_RUN_CONFIG>): void => {
    const latest = latestRef.current;
    if (latest.selectedModelReference == null) {
      return;
    }
    latest.onChangeConfig(latest.config, [MODEL_CONFIG_KEY], {
      model: latest.selectedModelReference,
      config: {...latest.runConfig, ...patch},
    });
  };

  const modelField = {
    variableName: 'Model',
    required: true,
    editor: (
      <NativeSelect
        aria-label="Model"
        data={[
          {value: '', label: 'Select a model', disabled: true},
          ...modelChoices.map((model) => ({value: model.externalReference, label: model.displayName})),
        ]}
        disabled={disabled}
        value={selectedModelReference ?? ''}
        onChange={(event) => {
          const model = event.currentTarget.value;
          if (model !== selectedModelReference) {
            designYamlReadIdRef.current += 1;
            // The run settings are hard-coded for the one custom model that exists today.
            onChangeConfig(config, [MODEL_CONFIG_KEY], {model, config: {...DEFAULT_RUN_CONFIG}});
          }
        }}
        size="lg"
      />
    ),
  };

  const batchFields = [
    {
      variableName: 'Prediction batch folder',
      required: true,
      editor: (
        <FolderEditor
          value={predictionBatchFolderId}
          onChange={(folderId) => {
            onChangeConfig(config, [PREDICTION_BATCH_FOLDER_ID], folderId);
            if (folderId != null) {
              FolderDefaults.set(FolderDefaultsKey.BULK_MODEL_PREDICTION, folderId);
            }
          }}
          required={true}
          disabled={disabled}
          showRegistries={false}
          showInventory={false}
          showNotebook={true}
        />
      ),
    },
    {
      variableName: 'Prediction batch name',
      required: false,
      editor: (
        <TextInput
          aria-label="Prediction batch name"
          disabled={disabled}
          maxLength={PREDICTION_BATCH_NAME_MAX_LENGTH}
          placeholder="Enter a name, or leave blank to auto-generate one"
          value={predictionBatchName}
          onChange={(event) => {
            const value = event.currentTarget.value;
            onChangeConfig(config, [PREDICTION_BATCH_NAME], value === '' ? null : value);
          }}
          size="lg"
        />
      ),
    },
  ];

  const runFields = [
    {
      variableName: 'Input file',
      required: true,
      infoText: 'The file the model runs on, such as a target structure.',
      editor: (
        <SearchEditor
          config={runConfig.target?.id ?? null}
          onChangeConfig={(fileId) => {
            updateRunConfig({target: fileId == null ? null : {_type: 'fileId', id: fileId}});
          }}
          disabled={disabled}
          modelTypes={[ModelTypeConstants.FILE]}
        />
      ),
    },
    {
      variableName: 'Target format',
      required: true,
      infoText: 'The file format of the input target structure.',
      editor: (
        <NativeSelect
          aria-label="Target format"
          data={INPUT_TYPE_OPTIONS}
          disabled={disabled}
          value={runConfig.inputType}
          onChange={(event) => {
            updateRunConfig({inputType: event.currentTarget.value});
          }}
          size="lg"
        />
      ),
    },
    {
      variableName: 'Number of designs',
      required: true,
      infoText: `Generate between ${MIN_DESIGNS} and ${MAX_DESIGNS} designs.`,
      editor: (
        <NumberInput
          aria-label="Number of designs"
          disabled={disabled}
          min={MIN_DESIGNS}
          max={MAX_DESIGNS}
          step={1}
          precision={0}
          value={runConfig.numDesigns}
          onChange={(value) => {
            if (typeof value === 'number') {
              updateRunConfig({numDesigns: value});
            }
          }}
          size="lg"
        />
      ),
    },
    {
      variableName: 'Design specification (YAML)',
      required: true,
      infoText:
        'The design specification for the run. Its first entity must be the input file, with a file extension that matches the target format.',
      editor: (
        <Stack spacing="xs">
          <Box>
            <FileButton
              accept=".yaml,.yml,text/yaml,text/plain"
              disabled={disabled}
              inputProps={{'aria-label': 'Upload design specification file'}}
              onChange={async (file) => {
                if (file != null) {
                  designYamlReadIdRef.current += 1;
                  const readId = designYamlReadIdRef.current;
                  const designYaml = await file.text();
                  // A newer upload or a different model selection supersedes this read.
                  if (readId === designYamlReadIdRef.current) {
                    updateRunConfig({designYaml});
                  }
                }
              }}
            >
              {(buttonProps) => (
                <Button {...buttonProps} variant="tertiary" aria-label="Upload design specification">
                  Upload YAML file
                </Button>
              )}
            </FileButton>
          </Box>
          <Textarea
            aria-label="Design specification (YAML)"
            autosize={true}
            minRows={8}
            maxRows={24}
            disabled={disabled}
            styles={{input: {fontFamily: 'monospace'}}}
            value={runConfig.designYaml}
            onChange={(event) => {
              updateRunConfig({designYaml: event.currentTarget.value});
            }}
          />
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing="sm">
      <Stack spacing={0}>
        <StepConfigFieldsTemplate numColumns={1} fields={batchFields} />
        <StepConfigFieldsTemplate numColumns={1} fields={[modelField]} />
        {selectedModel?.description && (
          <Text size="sm" color="dimmed" mb="lg" ml="xs">
            {selectedModel.description}
          </Text>
        )}
        {selectedModelReference != null && <StepConfigFieldsTemplate numColumns={1} fields={runFields} />}
      </Stack>
    </Stack>
  );
}

export const RUN_CUSTOM_MODEL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.RUN_CUSTOM_MODEL, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
  modelConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: [MODEL_CONFIG_KEY],
    modelConfig: {
      [CUSTOM_COMPONENT_KEY]: (props) => <CustomModelConfigEditor {...props} />,
    },
  },
};
